import {
  Building2,
  Cable,
  ChartColumn,
  ChevronsUpDown,
  LifeBuoy,
  LogOut,
  type LucideIcon,
  MessageSquareText,
  Package,
  ShieldCheck,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SIDEBAR_STORAGE_KEY,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { local } from "@/lib/storage";
import { clearSpace, EsiLockup, LOCKUP_MIN_WIDTH, lockupClearSpace } from "../brand/EsiMark.tsx";
import { currentLocale, LOCALES, type Locale, setLocale } from "../i18n/i18n.ts";
import {
  CONSOLE_FEEDBACK_PATH,
  CONSOLE_MODEL_CONNECTIONS_PATH,
  CONSOLE_PATH,
  CONSOLE_PLANS_PATH,
  CONSOLE_PROFILE_PATH,
  CONSOLE_REPORTS_PATH,
  CONSOLE_SUPPORT_PATH,
  type ConsoleView,
  consoleOperatorsPath,
  consolePoliciesPath,
  Link,
  navigate,
  SETTINGS_GROUPS,
} from "../router.tsx";
import { MAIN_ID } from "../shell/main-region.ts";
import { SessionEnded } from "../shell/SessionEnded.tsx";
import { RailTrigger } from "../shell/sidebar/RailTrigger.tsx";
import { signOut } from "../shell/sign-out.ts";
import { type ThemeChoice, useTheme } from "../theme/theme.tsx";
import { ConsoleProfile } from "./ConsoleProfile.tsx";
import { ConsoleReports } from "./ConsoleReports.tsx";
import { FeedbackGroupPage, FeedbackGroups } from "./FeedbackGroups.tsx";
import { FeedbackInbox, FeedbackReportPage } from "./FeedbackInbox.tsx";
import { ModelConnectionsPage } from "./ModelConnectionsPage.tsx";
import { OperatorsPage } from "./OperatorsPage.tsx";
import { useOperator } from "./operator.tsx";
import { PlansPage } from "./PlansPage.tsx";
import { PoliciesPage } from "./PoliciesPage.tsx";
import { SupportBackoffice, SupportPage } from "./SupportPage.tsx";
import { TenantDetail } from "./TenantDetail.tsx";
import { TenantsPage } from "./TenantsPage.tsx";

type ConsoleArea =
  | "tenants"
  | "plans"
  | "reports"
  | "feedback"
  | "policies"
  | "modelConnections"
  | "support"
  | "operators"
  // Off the rail: the operator menu opens it.
  | "profile";
type NavItem = { area: ConsoleArea; href: string; icon: LucideIcon };

/**
 * The console's rail, in three groups: the customers and what they use, what the instance
 * enforces, and who reaches what (support access to a tenant, the operators themselves).
 */
const NAV: { group: "customers" | "governance" | "access"; items: NavItem[] }[] = [
  {
    group: "customers",
    items: [
      { area: "tenants", href: CONSOLE_PATH, icon: Building2 },
      { area: "plans", href: CONSOLE_PLANS_PATH, icon: Package },
      { area: "reports", href: CONSOLE_REPORTS_PATH, icon: ChartColumn },
      { area: "feedback", href: CONSOLE_FEEDBACK_PATH, icon: MessageSquareText },
    ],
  },
  {
    group: "governance",
    // Straight to the first page: the bare URL only redirects there.
    items: [
      { area: "policies", href: consolePoliciesPath(SETTINGS_GROUPS[0]), icon: ShieldCheck },
      { area: "modelConnections", href: CONSOLE_MODEL_CONNECTIONS_PATH, icon: Cable },
    ],
  },
  {
    group: "access",
    items: [
      { area: "support", href: CONSOLE_SUPPORT_PATH, icon: LifeBuoy },
      { area: "operators", href: consoleOperatorsPath(), icon: UsersRound },
    ],
  },
];

function areaOf(view: ConsoleView): ConsoleArea {
  switch (view.name) {
    case "tenants":
    case "tenant":
      return "tenants";
    case "supportGrant":
      return "support";
    case "feedbackGroups":
      return "feedback";
    default:
      return view.name;
  }
}

/**
 * The instance console (spec `tenant-administration` "Operator console"): tenants, plans,
 * instance policies, support access, usage reports and operators (their sessions). Operators never see
 * projects or a tenant's backoffice, except one tenant's backoffice read-only under a support
 * grant that tenant approved.
 */
export function ConsoleApp({ view }: { view: ConsoleView }) {
  const { t } = useTranslation();
  const area = areaOf(view);
  useDocumentTitle(view.name === "supportGrant" ? undefined : t(`console.nav.${area}`));
  return (
    <>
      <a
        href={`#${MAIN_ID}`}
        onClick={(e) => {
          e.preventDefault();
          document.getElementById(MAIN_ID)?.focus();
        }}
        className="sr-only z-50 rounded-md bg-surface-overlay px-3 py-2 text-sm font-medium text-fg shadow-md focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t("app.skipToContent")}
      </a>
      <SidebarProvider
        defaultOpen={local.get(SIDEBAR_STORAGE_KEY) !== "collapsed"}
        className="h-dvh min-h-0 overflow-hidden"
      >
        <ConsoleSidebar area={area} />
        <SidebarInset
          id={MAIN_ID}
          tabIndex={-1}
          className="min-h-0 min-w-0 overflow-hidden outline-none"
        >
          <MobileBar />
          <SessionEnded />
          <ConsoleRoutes view={view} />
        </SidebarInset>
      </SidebarProvider>
    </>
  );
}

function ConsoleRoutes({ view }: { view: ConsoleView }) {
  switch (view.name) {
    case "tenants":
      return <TenantsPage />;
    case "tenant":
      return <TenantDetail key={view.tenantId} tenantId={view.tenantId} tab={view.tab} />;
    case "plans":
      return <PlansPage />;
    case "modelConnections":
      return <ModelConnectionsPage />;
    case "policies":
      return <PoliciesPage group={view.group} />;
    case "support":
      return <SupportPage />;
    case "supportGrant":
      return (
        <SupportBackoffice
          key={view.grantId}
          grantId={view.grantId}
          section={view.section}
          projectId={view.projectId}
          settingsGroup={view.settingsGroup}
        />
      );
    case "reports":
      return <ConsoleReports />;
    case "feedback":
      return view.reportId ? (
        <FeedbackReportPage key={view.reportId} reportId={view.reportId} />
      ) : (
        <FeedbackInbox />
      );
    case "feedbackGroups":
      return view.groupId ? (
        <FeedbackGroupPage key={view.groupId} groupId={view.groupId} />
      ) : (
        <FeedbackGroups />
      );
    case "operators":
      return <OperatorsPage tab={view.tab} />;
    case "profile":
      return <ConsoleProfile />;
  }
}

/**
 * The console's mark: the ESI lockup and the "Console" label, linked to the tenant list and
 * announced once as "ESI Console". Always ESI: the console is never tenant-branded.
 */
function ConsoleMark({ clear }: { clear: "start" | "end" }) {
  const { t } = useTranslation();
  return (
    <Link
      href={CONSOLE_PATH}
      className="flex items-center rounded-sm"
      style={clearSpace(LOCKUP_MIN_WIDTH, clear)}
    >
      <EsiLockup width={LOCKUP_MIN_WIDTH} />
      <span
        className="text-xs font-medium text-fg-muted"
        style={{ marginInlineStart: lockupClearSpace(LOCKUP_MIN_WIDTH) }}
      >
        {t("console.badge")}
      </span>
    </Link>
  );
}

function MobileBar() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  if (!isMobile) {
    return null;
  }
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
      <SidebarTrigger aria-label={t("nav.openMenu")} />
      <ConsoleMark clear="start" />
    </div>
  );
}

function ConsoleSidebar({ area }: { area: ConsoleArea }) {
  const { t } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  const close = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  return (
    <Sidebar collapsible="icon" aria-label={t("nav.sidebar")}>
      <SidebarHeader className="h-12 flex-row items-center justify-between gap-1 px-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2">
        <span className="group-data-[collapsible=icon]:hidden">
          <ConsoleMark clear="end" />
        </span>
        {isMobile ? null : <RailTrigger mark />}
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label={t("console.nav.label")}>
          {NAV.map((section) => (
            <SidebarGroup key={section.group}>
              {section.group === "customers" ? null : (
                <SidebarGroupLabel>{t(`console.nav.groups.${section.group}`)}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const label = t(`console.nav.${item.area}`);
                    const current = area === item.area;
                    return (
                      <SidebarMenuItem key={item.area}>
                        <SidebarMenuButton asChild isActive={current} tooltip={label}>
                          <Link
                            href={item.href}
                            aria-current={current ? "page" : undefined}
                            onClick={close}
                          >
                            <Icon aria-hidden="true" strokeWidth={1.75} />
                            <span>{label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter className="gap-1">
        <OperatorFooter />
      </SidebarFooter>
    </Sidebar>
  );
}

const THEMES: ThemeChoice[] = ["system", "light", "dark"];

/**
 * The operator menu at the rail's foot, like the app's account menu: who is signed in, the
 * theme and language (this browser only: operators have no account settings in ESI) and
 * sign out.
 */
function OperatorFooter() {
  const { t } = useTranslation();
  const { csrfToken, email, name: ownName } = useOperator();
  const { choice, setChoice } = useTheme();
  const { isMobile, setOpenMobile } = useSidebar();
  const [leaving, setLeaving] = useState(false);
  // The directory's name, else the sign-in e-mail; never the sub (an older API sends neither:
  // the role alone, with a neutral avatar).
  const known = ownName || email;
  const name = known || t("console.operator.role");
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={name}
              className="data-[state=open]:bg-sidebar-accent"
            >
              {/* Named from its content like the app's account menu (WCAG 2.5.3): the initials
                  are hidden from the name, the purpose and separator are sr-only. */}
              <span className="sr-only">{t("console.operator.menu")}, </span>
              {known ? <Avatar name={known} /> : <Avatar icon={UserRound} />}
              <span className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium text-fg">
                  {name}
                  {known ? <span className="sr-only">,</span> : null}
                </span>
                {known && (
                  <span className="truncate text-xs text-fg-muted">
                    {t("console.operator.role")}
                  </span>
                )}
              </span>
              <ChevronsUpDown aria-hidden="true" className="ml-auto size-4 text-fg-muted" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "top" : "right"}
            align="end"
            sideOffset={8}
            className="w-64"
          >
            <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
              <span className="text-xs text-fg-muted">{t("account.signedInAs")}</span>
              <span className="truncate text-sm font-medium text-fg">{name}</span>
              {ownName && email && (
                <span className="truncate text-xs text-fg-secondary">{email}</span>
              )}
              {known && (
                <span className="truncate text-xs text-fg-secondary">
                  {t("console.operator.role")}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-medium text-fg-muted">
                {t("account.theme")}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={choice}
                onValueChange={(value) => setChoice(value as ThemeChoice)}
              >
                {THEMES.map((theme) => (
                  <DropdownMenuRadioItem key={theme} value={theme}>
                    {t(`account.themes.${theme}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-medium text-fg-muted">
                {t("account.language")}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={currentLocale()}
                onValueChange={(value) => void setLocale(value as Locale)}
              >
                {LOCALES.map((locale) => (
                  <DropdownMenuRadioItem key={locale} value={locale} lang={locale}>
                    {t(`account.languages.${locale}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                // Like a rail link: the phone's sidebar sheet closes on the way.
                if (isMobile) {
                  setOpenMobile(false);
                }
                navigate(CONSOLE_PROFILE_PATH);
              }}
            >
              <UserRound aria-hidden="true" strokeWidth={1.75} />
              {t("console.operator.profile")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={leaving}
              onSelect={() => {
                setLeaving(true);
                void signOut(csrfToken);
              }}
            >
              <LogOut aria-hidden="true" strokeWidth={1.75} />
              {leaving ? t("account.signingOut") : t("account.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
